import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  Upload, FileText, Loader2, CheckCircle2, AlertTriangle, Sparkles, Trash2, Scale, Eye
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * InvoiceReconciliationTab — Rapprochement facture ↔ livraison
 * 1 facture = 1 livraison. Lecture seule : ne modifie jamais commande ni réception.
 * Flux : upload (PDF/photo) → vérification des lignes extraites → comparaison → écarts.
 */

const STATUS_BADGES = {
  conforme: { cls: 'bg-green-100 text-green-700 border-green-300 hover:bg-green-100', label: '✓ Conforme' },
  ecart: { cls: 'bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-100', label: '⚠ Écart' },
  orpheline: { cls: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-100', label: '✗ Orpheline' },
};

export const InvoiceReconciliationTab = ({ order, onUpdated }) => {
  const { api, user } = useAuth();
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [extraction, setExtraction] = useState(null); // {document, lines}
  const [lines, setLines] = useState([]);
  const [tolerance, setTolerance] = useState('5');
  const [reconciling, setReconciling] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerData, setViewerData] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const reconciliation = order.invoice_reconciliation;

  const handleAnalyze = async () => {
    if (!file) return;
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data: start } = await api.post(`/deliveries/${order.id}/invoice/extract`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000,
      });
      setJobId(start.job_id);
      let job = null;
      for (let i = 0; i < 100; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const { data } = await api.get(`/deliveries/invoice-jobs/${start.job_id}`);
        if (data.status === 'completed') { job = data; break; }
        if (data.status === 'failed') throw new Error(data.error || 'Extraction échouée');
      }
      if (!job) throw new Error("L'analyse a pris trop de temps, réessayez");

      setExtraction(job.result);
      setLines(job.result.lines.map((l, idx) => ({ ...l, id: idx, included: l.type_ligne === 'produit' })));
      toast.success(`${job.result.lines.length} ligne(s) extraite(s) — vérifiez avant comparaison`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Erreur lors de l'analyse");
    } finally {
      setAnalyzing(false);
    }
  };

  const updateLine = (id, field, value) => {
    setLines(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  const handleReconcile = async () => {
    const productLines = lines.filter(l => l.included);
    if (!productLines.length) { toast.error('Aucune ligne produit à comparer'); return; }
    setReconciling(true);
    try {
      await api.post(`/deliveries/${order.id}/invoice/reconcile`, {
        job_id: jobId,
        lines: productLines.map(l => ({
          code: l.code || null,
          designation: l.designation || '',
          quantite: parseFloat(l.quantite) || 0,
          prix_unitaire: parseFloat(l.prix_unitaire) || 0,
          total_ligne: l.total_ligne !== null && l.total_ligne !== '' ? parseFloat(l.total_ligne) : null,
        })),
        numero_document: extraction?.document?.numero_document,
        date_document: extraction?.document?.date_document,
        fournisseur_detecte: extraction?.document?.fournisseur_detecte,
        total_ht_document: extraction?.document?.total_ht_document,
        tolerance_pct: parseFloat(tolerance) || 5,
      });
      toast.success('Comparaison terminée');
      setExtraction(null);
      setLines([]);
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la comparaison');
    } finally {
      setReconciling(false);
    }
  };

  const openInvoiceFile = async () => {
    try {
      const { data } = await api.get(`/deliveries/${order.id}/invoice/file`);
      setViewerData(data);
      setViewerOpen(true);
    } catch {
      toast.error('Impossible de charger la facture');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce rapprochement pour ré-importer une facture ?')) return;
    setDeleting(true);
    try {
      await api.delete(`/deliveries/${order.id}/invoice`);
      toast.success('Rapprochement supprimé');
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setDeleting(false);
    }
  };

  // ============ VUE RÉSULTATS (facture rapprochée) ============
  if (reconciliation) {
    const s = reconciliation.summary || {};
    return (
      <div className="space-y-4" data-testid="reconciliation-results">
        <div className={`p-4 rounded-lg border-2 flex flex-wrap items-center gap-3 ${
          reconciliation.status === 'rapproche' ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'
        }`}>
          {reconciliation.status === 'rapproche' ? (
            <CheckCircle2 className="w-7 h-7 text-green-600" />
          ) : (
            <AlertTriangle className="w-7 h-7 text-orange-600" />
          )}
          <div className="flex-1">
            <p className="font-bold text-gray-900">
              {reconciliation.status === 'rapproche' ? 'FACTURE RAPPROCHÉE — conforme' : 'ÉCARTS DÉTECTÉS'}
            </p>
            <p className="text-sm text-gray-600">
              Facture {reconciliation.numero_document || ''} • {reconciliation.fournisseur_detecte || ''} •
              Total HT : {reconciliation.total_ht_document != null ? `${Number(reconciliation.total_ht_document).toFixed(2)} €` : 'N/A'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={openInvoiceFile} data-testid="view-invoice-file-btn">
              <Eye className="w-4 h-4 mr-1" /> Voir la facture
            </Button>
            {user?.is_admin && (
              <Button variant="outline" size="sm" onClick={handleDelete} disabled={deleting} className="text-red-600 border-red-300">
                <Trash2 className="w-4 h-4 mr-1" /> Supprimer
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="text-2xl font-bold text-green-700">{s.conformes ?? 0}</p>
            <p className="text-xs text-gray-600">Conformes</p>
          </div>
          <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
            <p className="text-2xl font-bold text-orange-700">{s.ecarts ?? 0}</p>
            <p className="text-xs text-gray-600">Écarts</p>
          </div>
          <div className="p-3 bg-red-50 rounded-lg border border-red-200">
            <p className="text-2xl font-bold text-red-700">{s.orphelines ?? 0}</p>
            <p className="text-xs text-gray-600">Orphelines</p>
          </div>
          <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-2xl font-bold text-purple-700">{s.non_factures ?? 0}</p>
            <p className="text-xs text-gray-600">Livrés non facturés</p>
          </div>
          <div className={`p-3 rounded-lg border ${Number(s.total_ecart_eur) > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
            <p className={`text-2xl font-bold ${Number(s.total_ecart_eur) > 0 ? 'text-red-700' : 'text-gray-700'}`} data-testid="total-ecart-eur">
              {Number(s.total_ecart_eur ?? 0).toFixed(2)} €
            </p>
            <p className="text-xs text-gray-600">Écart total (à réclamer)</p>
          </div>
        </div>

        <Card className="border border-gray-200">
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Article</TableHead>
                  <TableHead className="text-center">Qté facturée</TableHead>
                  <TableHead className="text-center">Qté facturable</TableHead>
                  <TableHead className="text-right">PU</TableHead>
                  <TableHead className="text-right">Écart €</TableHead>
                  <TableHead className="text-center">Statut</TableHead>
                  <TableHead>Détail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(reconciliation.lines || []).map((l, idx) => {
                  const badge = STATUS_BADGES[l.status] || STATUS_BADGES.conforme;
                  return (
                    <TableRow key={idx} className={l.status !== 'conforme' ? 'bg-orange-50/50' : ''} data-testid={`recon-line-${l.code || idx}`}>
                      <TableCell>
                        {l.code && <span className="text-xs font-mono text-gray-500 bg-gray-100 px-1 rounded mr-1">{l.code}</span>}
                        <span className="text-sm">{l.designation}</span>
                      </TableCell>
                      <TableCell className="text-center font-medium">{l.qte_facturee}</TableCell>
                      <TableCell className="text-center font-medium">{l.qte_facturable ?? '—'}</TableCell>
                      <TableCell className="text-right text-sm">{l.prix_unitaire ? `${Number(l.prix_unitaire).toFixed(2)} €` : '—'}</TableCell>
                      <TableCell className={`text-right font-semibold ${Number(l.ecart_eur) > 0 ? 'text-red-600' : Number(l.ecart_eur) < 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                        {Number(l.ecart_eur) !== 0 ? `${Number(l.ecart_eur) > 0 ? '+' : ''}${Number(l.ecart_eur).toFixed(2)} €` : '—'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={badge.cls}>{badge.label}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">{l.detail || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {(reconciliation.non_factures || []).length > 0 && (
          <Alert className="border-purple-300 bg-purple-50">
            <AlertTriangle className="w-4 h-4 text-purple-600" />
            <AlertDescription>
              <strong>Livré mais absent de la facture :</strong>{' '}
              {reconciliation.non_factures.map(n => `${n.code ? n.code + ' ' : ''}${n.designation} (${n.qte_facturable})`).join(' • ')}
            </AlertDescription>
          </Alert>
        )}

        <p className="text-xs text-gray-500">
          Rapproché par {reconciliation.reconciled_by} • Tolérance poids variable : {reconciliation.tolerance_pct}% •
          La facture est en lecture seule, elle ne modifie ni la commande ni la réception.
        </p>

        <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>{viewerData?.filename || 'Facture'}</DialogTitle>
            </DialogHeader>
            {viewerData && (
              viewerData.media_type === 'application/pdf' ? (
                <iframe
                  src={`data:application/pdf;base64,${viewerData.file_base64}`}
                  title="facture" className="w-full h-[70vh] rounded border"
                />
              ) : (
                <img src={`data:${viewerData.media_type};base64,${viewerData.file_base64}`} alt="facture" className="max-h-[70vh] mx-auto rounded border" />
              )
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ============ ÉTAPE VÉRIFICATION ============
  if (extraction) {
    const doc = extraction.document || {};
    const productLines = lines.filter(l => l.included);
    const sumLines = productLines.reduce((acc, l) => acc + (parseFloat(l.total_ligne) || 0), 0);
    const totalDoc = doc.total_ht_document;
    const fraisSum = lines.filter(l => !l.included).reduce((acc, l) => acc + (parseFloat(l.total_ligne) || 0), 0);
    const coherent = totalDoc != null && Math.abs(sumLines + fraisSum - totalDoc) <= Math.max(0.05, Math.abs(totalDoc) * 0.02);
    const supplierMatch = (doc.fournisseur_detecte || '').toUpperCase().includes((order.supplier_name || '').toUpperCase().split(' ')[0])
      || (order.supplier_name || '').toUpperCase().includes((doc.fournisseur_detecte || '').toUpperCase().split(' ')[0]);

    return (
      <div className="space-y-4" data-testid="reconciliation-verify">
        <div className="flex flex-wrap items-center gap-3 p-3 bg-gray-50 rounded-lg border text-sm">
          <FileText className="w-4 h-4 text-gray-500" />
          <span className="font-medium">{doc.fournisseur_detecte || 'Fournisseur inconnu'}</span>
          {doc.numero_document && <Badge variant="outline">N° {doc.numero_document}</Badge>}
          {totalDoc != null && <Badge variant="outline">Total HT : {Number(totalDoc).toFixed(2)} €</Badge>}
          {coherent ? (
            <Badge className="bg-green-100 text-green-700 border-green-300 hover:bg-green-100">✓ Somme des lignes cohérente</Badge>
          ) : (
            <Badge className="bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-100" data-testid="coherence-warning">
              ⚠ Somme lignes {(sumLines + fraisSum).toFixed(2)} € ≠ total {totalDoc != null ? Number(totalDoc).toFixed(2) : '?'} €
            </Badge>
          )}
        </div>

        {!supplierMatch && doc.fournisseur_detecte && (
          <Alert className="border-red-300 bg-red-50" data-testid="supplier-mismatch-warning">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <AlertDescription>
              <strong>Attention :</strong> le fournisseur détecté sur la facture (« {doc.fournisseur_detecte} ») ne semble pas
              correspondre à celui de la livraison (« {order.supplier_name} »). 1 facture = 1 livraison.
            </AlertDescription>
          </Alert>
        )}

        <Card className="border border-gray-200">
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Désignation</TableHead>
                  <TableHead className="text-center">Qté</TableHead>
                  <TableHead className="text-right">PU HT</TableHead>
                  <TableHead className="text-right">Total HT</TableHead>
                  <TableHead className="text-center">Vérif</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map(l => (
                  <TableRow key={l.id} className={!l.included ? 'opacity-50 bg-gray-50' : ''}>
                    <TableCell>
                      <input
                        type="checkbox" checked={l.included}
                        onChange={(e) => updateLine(l.id, 'included', e.target.checked)}
                        title={l.type_ligne !== 'produit' ? `Ligne ${l.type_ligne} (exclue par défaut)` : 'Inclure dans la comparaison'}
                      />
                    </TableCell>
                    <TableCell>
                      <Input value={l.code || ''} onChange={(e) => updateLine(l.id, 'code', e.target.value)} className="h-8 w-24 text-xs font-mono" />
                    </TableCell>
                    <TableCell>
                      <Input value={l.designation || ''} onChange={(e) => updateLine(l.id, 'designation', e.target.value)} className="h-8 text-sm min-w-[220px]" />
                      {l.type_ligne !== 'produit' && <span className="text-xs text-gray-500">({l.type_ligne})</span>}
                    </TableCell>
                    <TableCell>
                      <Input value={l.quantite ?? ''} onChange={(e) => updateLine(l.id, 'quantite', e.target.value.replace(',', '.'))} className="h-8 w-20 text-center" />
                    </TableCell>
                    <TableCell>
                      <Input value={l.prix_unitaire ?? ''} onChange={(e) => updateLine(l.id, 'prix_unitaire', e.target.value.replace(',', '.'))} className="h-8 w-24 text-right" />
                    </TableCell>
                    <TableCell>
                      <Input value={l.total_ligne ?? ''} onChange={(e) => updateLine(l.id, 'total_ligne', e.target.value.replace(',', '.'))} className="h-8 w-24 text-right font-medium" />
                    </TableCell>
                    <TableCell className="text-center">
                      {l.verifie ? (
                        <span className="text-green-600 font-bold" title="Qté × PU = total vérifié">✓</span>
                      ) : (
                        <span className="text-orange-500 font-bold cursor-help" title={l.note || 'À contrôler'}>⚠</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Scale className="w-4 h-4 text-gray-500" />
            <Label className="text-sm">Tolérance poids variable</Label>
            <Input
              value={tolerance} onChange={(e) => setTolerance(e.target.value.replace(',', '.'))}
              className="h-9 w-16 text-center" data-testid="tolerance-input"
            />
            <span className="text-sm text-gray-500">%</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { setExtraction(null); setLines([]); }}>← Changer de fichier</Button>
            <Button
              onClick={handleReconcile} disabled={reconciling}
              className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
              data-testid="launch-comparison-button"
            >
              {reconciling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Comparaison...</> : <>Lancer la comparaison ({productLines.length} lignes)</>}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ============ ÉTAPE UPLOAD ============
  return (
    <div className="space-y-4" data-testid="reconciliation-upload">
      <Alert>
        <Sparkles className="w-4 h-4" />
        <AlertDescription>
          Importez la facture du fournisseur (<strong>PDF ou photo</strong>) pour la comparer automatiquement
          aux quantités réellement livrées et acceptées. Règle : <strong>1 facture = 1 livraison</strong>.
        </AlertDescription>
      </Alert>
      <label
        className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/50 transition-colors block"
        data-testid="invoice-upload-zone"
      >
        <input
          type="file" accept=".pdf,image/jpeg,image/png,image/webp" className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="invoice-file-input"
        />
        {file ? (
          <div className="flex items-center justify-center gap-2 text-emerald-700">
            <FileText className="w-6 h-6" />
            <span className="font-medium">{file.name}</span>
            <span className="text-sm text-gray-500">({(file.size / 1024).toFixed(0)} Ko)</span>
          </div>
        ) : (
          <div className="text-gray-500">
            <Upload className="w-10 h-10 mx-auto mb-2 text-gray-400" />
            <p className="font-medium">Cliquez pour sélectionner la facture</p>
            <p className="text-sm mt-1">PDF, JPG ou PNG (max 20 Mo)</p>
          </div>
        )}
      </label>
      <Button
        className="w-full bg-[#1B4332] hover:bg-[#0D2E1F]"
        disabled={!file || analyzing}
        onClick={handleAnalyze}
        data-testid="analyze-delivery-invoice-button"
      >
        {analyzing ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse en cours (jusqu'à 2 min)...</>
        ) : (
          <><Sparkles className="w-4 h-4 mr-2" /> Analyser la facture</>
        )}
      </Button>
    </div>
  );
};

export default InvoiceReconciliationTab;
